import { motion, useReducedMotion } from "framer-motion";

export const FleurIcon = ({ className = "w-6 h-6", id = "ornGold" }) => (
  <svg viewBox="0 0 48 48" className={className} fill="none" aria-hidden="true">
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#f3d079" />
        <stop offset="0.55" stopColor="#d9a53a" />
        <stop offset="1" stopColor="#8a5a1f" />
      </linearGradient>
    </defs>
    <g fill={`url(#${id})`}>
      <path d="M24 4c3.4 4.6 4.6 9.4 0 15-4.6-5.6-3.4-10.4 0-15z" />
      <path d="M24 44c-2.2-3-3-6 0-10 3 4 2.2 7 0 10z" />
      <path d="M20 21c-4.5-2.4-9.6-2-13 1.6 2.2.1 3.9 1 4.6 2.8 1.6-1.8 3.8-2.1 6.2-1.2-2.4 1.5-3.7 3.6-3.8 6.2 3.4-.4 6.4-2.7 7.8-6.4z" />
      <path d="M28 21c4.5-2.4 9.6-2 13 1.6-2.2.1-3.9 1-4.6 2.8-1.6-1.8-3.8-2.1-6.2-1.2 2.4 1.5 3.7 3.6 3.8 6.2-3.4-.4-6.4-2.7-7.8-6.4z" />
      <circle cx="24" cy="24" r="2.4" />
    </g>
  </svg>
);

const Line = ({ side, reduced }) => (
  <motion.span
    initial={reduced ? false : { scaleX: 0 }}
    whileInView={reduced ? undefined : { scaleX: 1 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
    style={{ transformOrigin: side === "left" ? "right center" : "left center" }}
    className={`block h-px w-12 sm:w-20 ${
      side === "left"
        ? "bg-gradient-to-r from-transparent via-[#d9a53a]/50 to-[#d9a53a]"
        : "bg-gradient-to-l from-transparent via-[#d9a53a]/50 to-[#d9a53a]"
    }`}
  />
);

export const FleurDivider = ({ className = "", size = "w-6 h-6", id = "ornGoldDiv" }) => {
  const reduced = useReducedMotion();
  return (
    <div className={`flex items-center justify-center gap-2.5 ${className}`} aria-hidden="true">
      <Line side="left" reduced={reduced} />
      <motion.span
        initial={reduced ? false : { opacity: 0, scale: 0.5 }}
        whileInView={reduced ? undefined : { opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="motion-safe:animate-glow-pulse"
      >
        <FleurIcon id={id} className={`${size} drop-shadow-[0_0_8px_rgba(217,165,58,0.45)]`} />
      </motion.span>
      <Line side="right" reduced={reduced} />
    </div>
  );
};

export const CornerOrnament = ({ className = "", flip = "", id = "ornCorner" }) => {
  const transforms = { x: "scale(-1,1)", y: "scale(1,-1)", xy: "scale(-1,-1)" };
  return (
    <svg
      viewBox="0 0 100 100"
      className={className}
      style={flip ? { transform: transforms[flip] } : undefined}
      aria-hidden="true"
      fill="none"
    >
      <defs>
        <linearGradient id={id} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#8a5a1f" />
          <stop offset="0.5" stopColor="#d9a53a" />
          <stop offset="1" stopColor="#f3d079" />
        </linearGradient>
      </defs>
      <path d="M8 92 C8 46 46 8 92 8" stroke={`url(#${id})`} strokeWidth="2.6" strokeLinecap="round" />
      <path d="M17 92 C17 55 55 17 92 17" stroke={`url(#${id})`} strokeWidth="1.1" strokeLinecap="round" opacity="0.65" />
      <path d="M28 74 C42 66 51 56 55 40 C59 56 68 66 82 72 C66 74 53 81 46 91 C43 83 36 77 28 74 Z" fill={`url(#${id})`} opacity="0.9" />
      <path d="M62 30 C70 24 80 22 90 24 C82 28 76 34 74 42 C70 36 66 32 62 30 Z" fill={`url(#${id})`} opacity="0.75" />
      <path d="M30 62 C24 70 22 80 24 90 C28 82 34 76 42 74 C36 70 32 66 30 62 Z" fill={`url(#${id})`} opacity="0.75" />
      <circle cx="8" cy="92" r="3.4" fill={`url(#${id})`} />
      <circle cx="92" cy="8" r="3.4" fill={`url(#${id})`} />
      <circle cx="90" cy="28" r="1.7" fill={`url(#${id})`} opacity="0.8" />
      <circle cx="28" cy="90" r="1.7" fill={`url(#${id})`} opacity="0.8" />
    </svg>
  );
};

const EdgeLine = ({ top, reduced }) => (
  <div
    className={`absolute left-[18%] right-[18%] sm:left-[22%] sm:right-[22%] flex items-center gap-2 ${
      top ? "-top-[7px] sm:-top-2" : "-bottom-[7px] sm:-bottom-2"
    }`}
  >
    <span className={`h-px flex-1 bg-gradient-to-r ${top ? "from-transparent via-[#d9a53a]/60 to-[#d9a53a]" : "from-transparent via-[#d9a53a]/60 to-[#d9a53a]"}`} />
    <FleurIcon id={top ? "ornFrameT" : "ornFrameB"} className="w-3.5 h-3.5 sm:w-4 sm:h-4 drop-shadow-[0_0_6px_rgba(217,165,58,0.5)]" />
    <span className="h-px flex-1 bg-gradient-to-l from-transparent via-[#d9a53a]/60 to-[#d9a53a]" />
  </div>
);

export const HeaderFrame = () => (
  <div
    className="absolute -inset-x-2.5 -inset-y-2.5 sm:-inset-x-5 sm:-inset-y-3.5 pointer-events-none"
    aria-hidden="true"
    data-testid="header-ornament-frame"
  >
    <CornerOrnament id="ornC1" className="absolute top-0 left-0 w-9 h-9 sm:w-14 sm:h-14 opacity-90" />
    <CornerOrnament id="ornC2" flip="x" className="absolute top-0 right-0 w-9 h-9 sm:w-14 sm:h-14 opacity-90" />
    <CornerOrnament id="ornC3" flip="y" className="absolute bottom-0 left-0 w-9 h-9 sm:w-14 sm:h-14 opacity-90" />
    <CornerOrnament id="ornC4" flip="xy" className="absolute bottom-0 right-0 w-9 h-9 sm:w-14 sm:h-14 opacity-90" />
    <EdgeLine top />
    <EdgeLine />
  </div>
);
